/**
 * Landing — Pórtico
 * Direção estética: editorial arquitetônico brasileiro.
 * Paleta travertino/oak/walnut. Grid assimétrico, drop caps, numeração editorial.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const SUPA_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPA_ANON = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

// ─── Constantes de campanha ──────────────────────────────────────────────────
// CTA primário agora é o signup direto (conversão self-serve).
// WhatsApp reservado apenas como canal de suporte no footer.
// SIGNUP_LINK leva direto pro form de cadastro (modo sign_up).
// LOGIN_LINK fica reservado pro topbar "Entrar" (modo sign_in default).
const SIGNUP_LINK = '/auth/login?mode=sign_up';
const LOGIN_LINK = '/auth/login';
const WA_NUMBER = '5511940373492';
const WA_LINK = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(
  'Olá, preciso de ajuda com o Pórtico.',
)}`;

// Asaas payment links — 3 planos recorrentes mensais (Boleto/PIX + Cartão)
const PAYMENT_LINKS = {
  starter: 'https://www.asaas.com/c/sc9sruh61ldop0hc',
  pro: 'https://www.asaas.com/c/0gurrnt6ea2hnas6',
  agencia: 'https://www.asaas.com/c/xs4soxlxn00dqouw',
};

// ─── Dataset dos demos (3 casos do POC validados) ────────────────────────────
const DEMOS = [
  {
    issue: '№ 01',
    title: 'Sala de Jantar',
    subtitle: 'Apartamento São Paulo',
    credit: 'Projeto estudante · Estética modernista brasileira',
    before: '/demo/demo1-before.jpg',
    after: '/demo/demo1-after.png',
    caption:
      'Mesa redonda com tampo mármore, cadeiras Wishbone em carvalho, obras de arte preservadas do input.',
    meta: {
      time: '35s',
      res: '2 784 × 1 504',
    },
  },
  {
    issue: '№ 02',
    title: 'Cozinha Integrada',
    subtitle: 'Apartamento Vila Madalena',
    credit: 'Tratamento fotográfico editorial',
    before: '/demo/demo2-before.jpg',
    after: '/demo/demo2-after.png',
    caption:
      'Coifa cilíndrica inox, ilha de mármore branco, banquetas em couro caramelo. LED linear preservado.',
    meta: {
      time: '35s',
      res: '2 784 × 1 504',
    },
  },
  {
    issue: '№ 03',
    title: 'Sala de Estar',
    subtitle: 'Residência Jardim Europa',
    credit: 'Interior contemporâneo brasileiro',
    before: '/demo/demo3-before.jpg',
    after: '/demo/demo3-after.png',
    caption:
      'Painel ripado em carvalho, estante com curadoria editorial, luz natural volumétrica preservada fielmente.',
    meta: {
      time: '35s',
      res: '2 784 × 1 504',
    },
  },
];

// ─── Before / After Slider (drag suave, clip-path) ───────────────────────────
function BeforeAfter({ before, after, label }: { before: string; after: string; label?: string }) {
  const [pos, setPos] = useState(50);
  const ref = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const set = useCallback((clientX: number) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  }, []);

  useEffect(() => {
    const up = () => { dragging.current = false; };
    const mv = (e: MouseEvent) => { if (dragging.current) set(e.clientX); };
    const tmv = (e: TouchEvent) => {
      if (dragging.current && e.touches[0]) set(e.touches[0].clientX);
    };
    window.addEventListener('mouseup', up);
    window.addEventListener('touchend', up);
    window.addEventListener('mousemove', mv);
    window.addEventListener('touchmove', tmv, { passive: true });
    return () => {
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchend', up);
      window.removeEventListener('mousemove', mv);
      window.removeEventListener('touchmove', tmv);
    };
  }, [set]);

  return (
    <div
      ref={ref}
      className="ba-slider-wrapper relative aspect-[16/10] w-full cursor-ew-resize overflow-hidden select-none"
      style={{ ['--pos' as string]: `${pos}%` }}
      onMouseDown={(e) => { dragging.current = true; set(e.clientX); }}
      onTouchStart={(e) => { dragging.current = true; if (e.touches[0]) set(e.touches[0].clientX); }}
    >
      <img
        src={after}
        alt="depois"
        className="absolute inset-0 h-full w-full object-cover"
        draggable={false}
        loading="lazy"
      />
      <img
        src={before}
        alt="antes"
        className="ba-before-layer absolute inset-0 h-full w-full object-cover"
        draggable={false}
        loading="lazy"
      />

      {/* Marker line + handle */}
      <div
        className="absolute inset-y-0 flex w-px items-center justify-center bg-background/90"
        style={{ left: `${pos}%`, transform: 'translateX(-0.5px)' }}
      >
        <div className="flex h-12 w-12 -translate-x-1/2 items-center justify-center rounded-full border border-foreground/30 bg-background/95 shadow-[0_8px_30px_rgb(0_0_0_/_0.12)] backdrop-blur-sm">
          <span className="font-mono text-[10px] tracking-widest text-foreground">⇔</span>
        </div>
      </div>

      {/* Corner labels */}
      <span className="pointer-events-none absolute left-4 top-4 border border-background/40 bg-foreground/80 px-2 py-1 font-mono text-[10px] tracking-[0.2em] text-background backdrop-blur">
        SKETCHUP
      </span>
      <span className="pointer-events-none absolute right-4 top-4 bg-primary px-2 py-1 font-mono text-[10px] tracking-[0.2em] text-primary-foreground">
        PÓRTICO
      </span>
      {label && (
        <span className="pointer-events-none absolute bottom-4 left-4 rounded-[2px] bg-background/85 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-foreground backdrop-blur">
          {label}
        </span>
      )}
    </div>
  );
}

// ─── Página ───────────────────────────────────────────────────────────────────
export function LandingPage() {
  return (
    <div className="relative min-h-screen bg-background text-foreground">
      <EditorialNav />
      <Hero />
      <Marquee />
      <ManifestoStats />
      <EditorialShowcase />
      <HowItWorks />
      <Personas />
      <Guarantees />
      <Pricing />
      <FAQ />
      <ClosingCTA />
      <FooterBar />
    </div>
  );
}

// ─── Nav ──────────────────────────────────────────────────────────────────────
function EditorialNav() {
  return (
    <nav className="sticky top-0 z-50 border-b border-border/60 bg-background/88 backdrop-blur-md">
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 md:px-10 md:py-5">
        <a href="/" className="flex items-baseline gap-2 font-display text-xl font-semibold tracking-tight">
          <span className="text-primary">Pórtico</span>
          <span className="ml-2 hidden font-mono text-[10px] font-medium tracking-widest text-muted-foreground sm:inline">
            EST. 2026 · SP
          </span>
        </a>
        <div className="flex items-center gap-8">
          <a
            href="#showcase"
            className="link-editorial hidden text-xs font-medium uppercase tracking-[0.18em] text-foreground md:inline"
          >
            Edições
          </a>
          <a
            href="#pricing"
            className="link-editorial hidden text-xs font-medium uppercase tracking-[0.18em] text-foreground md:inline"
          >
            Assinatura
          </a>
          <a
            href={LOGIN_LINK}
            className="link-editorial hidden text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground md:inline"
          >
            Entrar
          </a>
          <a
            href={SIGNUP_LINK}
            className="group relative inline-flex items-center gap-2 bg-foreground px-5 py-2.5 font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-background transition-all hover:bg-primary hover:text-primary-foreground"
          >
            <span>Testar Grátis</span>
            <span className="transition-transform group-hover:translate-x-0.5">→</span>
          </a>
        </div>
      </div>
    </nav>
  );
}

// ─── Hero — manifesto editorial ───────────────────────────────────────────────
function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-6 pb-14 pt-12 md:grid-cols-12 md:gap-16 md:px-10 md:pb-24 md:pt-20">
        {/* Left — manifesto + copy */}
        <div className="relative md:col-span-6 md:pt-6">
          <div className="mb-10 flex items-center gap-6">
            <div className="h-px w-12 bg-foreground" />
            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-muted-foreground">
              Edição Nº 001 · 2026
            </span>
          </div>

          <h1 className="font-display text-[clamp(3rem,7.2vw,6.5rem)] font-light leading-[0.94] tracking-[-0.025em] text-foreground rise">
            Do modelo
            <br />
            ao retrato
            <br />
            <span className="italic-drama text-primary">em trinta segundos.</span>
          </h1>

          <p className="mt-10 max-w-lg text-lg leading-relaxed text-foreground/80 rise rise-1">
            O portal entre o seu modelo e o retrato. Um motor com estética
            brasileira — madeira oak com o tom certo, travertino, luz volumétrica
            de tarde, paisagismo tropical. Cada móvel, textura e detalhe do seu
            modelo preservado. Qualidade de publicação, dez vezes mais barato
            que estúdio de render.
          </p>

          <div className="mt-10 flex flex-col items-start gap-4 sm:flex-row sm:items-center rise rise-2">
            <a
              href={SIGNUP_LINK}
              className="group inline-flex items-center gap-3 bg-foreground px-6 py-4 font-mono text-xs font-medium uppercase tracking-[0.22em] text-background transition-all hover:bg-primary hover:text-primary-foreground"
            >
              <span>Criar conta grátis</span>
              <span className="transition-transform group-hover:translate-x-1">→</span>
            </a>
            <a
              href="#showcase"
              className="link-editorial font-mono text-xs uppercase tracking-[0.22em] text-foreground"
            >
              Ver as edições ↓
            </a>
          </div>

          <div className="mt-12 grid max-w-md grid-cols-3 gap-6 border-t border-border pt-6 rise rise-3">
            <Stat label="Entrega" value="30s" />
            <Stat label="Curadoria" value="BR premium" />
            <Stat label="Grátis início" value="3 renders" />
          </div>
        </div>

        {/* Right — imagem hero with editorial treatment */}
        <div className="relative md:col-span-6">
          <div className="relative rise rise-1">
            {/* Caption tab — top */}
            <div className="absolute -top-4 left-0 z-10 flex items-center gap-3 bg-background pr-4 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              <span className="h-px w-10 bg-foreground" />
              <span>Figura 001</span>
              <span>·</span>
              <span>Antes / Depois</span>
            </div>

            <BeforeAfter before={DEMOS[0]!.before} after={DEMOS[0]!.after} label="Arraste" />

            {/* Footer caption — like print page */}
            <div className="mt-3 flex items-baseline justify-between text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
              <span>{DEMOS[0]!.subtitle}</span>
              <span>{DEMOS[0]!.meta.res} px</span>
            </div>
          </div>
        </div>
      </div>

      {/* diagonal decorative rule at bottom */}
      <div className="rule mx-auto max-w-[1400px] px-6 md:px-10" />
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="font-display text-2xl font-medium leading-none tracking-tight text-foreground md:text-[1.75rem]">
        {value}
      </div>
      <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </div>
    </div>
  );
}

// ─── Marquee minimal — institutions/credits (social proof sutil) ──────────────
function Marquee() {
  const items = [
    'ESTÉTICA BRASILEIRA',
    'MODERNISMO REFINADO',
    'TRAVERTINO',
    'CARVALHO OAK',
    'LUZ DE TARDE',
    'PAISAGISMO TROPICAL',
    'INTERIOR EDITORIAL',
    'TOM CERTO',
    'MATERIAIS HONESTOS',
  ];
  return (
    <div className="overflow-hidden border-b border-border bg-secondary/30 py-5">
      <div className="flex gap-14 whitespace-nowrap animate-[marquee_38s_linear_infinite] [--tw-animation:marquee]">
        {[...items, ...items, ...items].map((t, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-14 font-mono text-[11px] font-medium uppercase tracking-[0.32em] text-muted-foreground"
          >
            {t}
            <span className="text-primary">✦</span>
          </span>
        ))}
      </div>
      <style>{`
        @keyframes marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-33.33%); }
        }
      `}</style>
    </div>
  );
}

// ─── Manifesto + números editoriais ──────────────────────────────────────────
function ManifestoStats() {
  return (
    <section className="relative border-b border-border">
      <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-10 md:py-32">
        <div className="grid grid-cols-1 gap-16 md:grid-cols-12 md:gap-24">
          <div className="md:col-span-5">
            <span className="section-no block mb-6">§ 01 · Manifesto</span>
            <h2 className="font-display text-4xl font-light leading-tight tracking-tight md:text-5xl">
              Pare de pagar
              <br />
              <span className="italic-drama text-primary">três mil reais</span>
              <br />
              por uma única perspectiva.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <p className="drop-cap text-lg leading-relaxed text-foreground/85">
              Estúdios de renderização cobram de R$ 1.500 a R$ 8.000 por vista.
              Entregam em sete a dez dias. O cliente muda a cor do sofá — você
              reinicia do zero. Há trinta anos a profissão arquitetônica aceita
              esse atrito como custo de fazer negócio. Nós, não. O Pórtico é o
              portal entre o seu modelo e o retrato — com estética brasileira
              afinada de propósito pra imóveis, interiores e paisagismo daqui.
              Render em trinta segundos. Você volta a desenhar. Não a esperar.
            </p>
            <div className="mt-10 grid grid-cols-2 gap-x-8 gap-y-8 border-t border-border pt-8 sm:grid-cols-3">
              <Metric value="30s" label="Por render" />
              <Metric value="2 784 px" label="Resolução" />
              <Metric value="BR" label="Curadoria premium" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Metric({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <div className="font-display text-3xl font-light tracking-tight text-foreground md:text-4xl">
        {value}
      </div>
      <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </div>
    </div>
  );
}

// ─── Showcase editorial — 3 projetos ─────────────────────────────────────────
function EditorialShowcase() {
  return (
    <section id="showcase" className="relative border-b border-border bg-secondary/25">
      <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-10 md:py-32">
        <div className="mb-16 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="section-no block mb-4">§ 02 · Edições</span>
            <h2 className="font-display text-4xl font-light leading-tight tracking-tight md:text-5xl">
              Três casos. <span className="italic-drama text-primary">Três SketchUps</span>.
              <br />
              Zero retoques.
            </h2>
          </div>
          <p className="max-w-xs text-sm text-muted-foreground">
            Cada imagem abaixo foi gerada diretamente a partir de um modelo 3D.
            Arraste o marcador para comparar.
          </p>
        </div>

        <div className="space-y-24 md:space-y-32">
          {DEMOS.map((d, i) => (
            <article
              key={d.issue}
              className={`grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-16 ${
                i % 2 === 1 ? 'md:[&>*:first-child]:order-2' : ''
              }`}
            >
              <div className="md:col-span-7">
                <BeforeAfter before={d.before} after={d.after} />
                <div className="mt-3 flex items-baseline justify-between text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
                  <span>{d.credit}</span>
                  <span>{d.meta.res} px</span>
                </div>
              </div>
              <div className="md:col-span-5 md:pt-10">
                <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-muted-foreground">
                  {d.issue}
                </span>
                <h3 className="mt-4 font-display text-3xl font-light tracking-tight md:text-4xl">
                  {d.title}
                </h3>
                <p className="mt-1 font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  {d.subtitle}
                </p>
                <p className="mt-6 text-base leading-relaxed text-foreground/80">
                  {d.caption}
                </p>
                <dl className="mt-8 divide-y divide-border border-y border-border text-sm">
                  <div className="flex justify-between py-3">
                    <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      Tempo
                    </dt>
                    <dd className="font-display">{d.meta.time}</dd>
                  </div>
                  <div className="flex justify-between py-3">
                    <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      Resolução
                    </dt>
                    <dd className="font-display">{d.meta.res} px</dd>
                  </div>
                  <div className="flex justify-between py-3">
                    <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      Formato
                    </dt>
                    <dd className="font-display">21:9 cinematic</dd>
                  </div>
                </dl>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Como funciona — steps editoriais ────────────────────────────────────────
function HowItWorks() {
  const steps = [
    {
      n: '01',
      title: 'Envie o modelo',
      caption:
        'SketchUp, Revit, planta baixa, esboço à mão, foto de obra vazia. Aceita JPG, PNG, WebP.',
    },
    {
      n: '02',
      title: 'Escolha o mood',
      caption:
        'Manhã, golden hour, noite. Formato 16:9 ou 21:9 cinematic. 1 ou 3 variantes.',
    },
    {
      n: '03',
      title: 'Receba em 30 s',
      caption:
        'Alta resolução, pronto para apresentar — ou para a capa de revista. PDF, Instagram, WhatsApp.',
    },
  ];
  return (
    <section className="relative border-b border-border">
      <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-10 md:py-32">
        <div className="mb-16">
          <span className="section-no block mb-4">§ 03 · Método</span>
          <h2 className="font-display text-4xl font-light leading-tight tracking-tight md:text-5xl">
            Três passos. <span className="italic-drama text-primary">Zero fricção</span>.
          </h2>
        </div>

        <ol className="grid grid-cols-1 gap-10 md:grid-cols-3 md:gap-14">
          {steps.map((s, i) => (
            <li key={s.n} className="relative">
              <div className="flex items-start gap-6">
                <span className="font-display text-5xl font-light leading-none tracking-tight text-primary md:text-6xl">
                  {s.n}
                </span>
                <div className="flex-1 pt-1">
                  <h3 className="font-display text-2xl font-light tracking-tight">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-foreground/75">
                    {s.caption}
                  </p>
                </div>
              </div>
              {i < steps.length - 1 && (
                <span className="mt-8 block h-px w-full bg-border md:hidden" />
              )}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

// ─── Personas — pra quem é ─────────────────────────────────────────────────
function Personas() {
  const personas = [
    {
      n: '01',
      title: 'Arquitetos',
      bullets: [
        'Apresenta 3 opções de material ao cliente sem refazer o render do zero',
        'Gera estudos de iluminação (manhã / golden / noite) em 90 segundos',
        'Entrega moodboard + 5 perspectivas pra reunião — no mesmo dia',
      ],
    },
    {
      n: '02',
      title: 'Designers de interiores',
      bullets: [
        'Transforma foto de ambiente vazio em proposta com mobília e paisagismo',
        'Troca mesa, sofá, cor da parede com uma linha de texto',
        'Staging fotorrealista pra Instagram, Pinterest, portfolio',
      ],
    },
    {
      n: '03',
      title: 'Imobiliárias e corretores',
      bullets: [
        'Staging virtual de imóveis vazios — foto realista, conversão maior',
        'Compra da planta baixa → render do apartamento decorado em 30s',
        'Material pra anúncio Zap/Viva/OLX em minutos, não em dias',
      ],
    },
    {
      n: '04',
      title: 'Escritórios boutique e autônomos',
      bullets: [
        'Qualidade de renderista premium sem ter um no time',
        'Apresente 3 moods ao cliente na mesma reunião, sem refazer',
        'Roda no navegador — sem GPU, sem software pesado, sem fila',
      ],
    },
  ];

  return (
    <section className="relative border-b border-border bg-secondary/25">
      <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-10 md:py-28">
        <div className="mb-14">
          <span className="section-no block mb-4">§ 04 · Para quem é</span>
          <h2 className="font-display text-4xl font-light leading-tight tracking-tight md:text-5xl">
            Feito pra quem faz
            <br />
            <span className="italic-drama text-primary">arquitetura no Brasil</span>.
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-x-12 md:gap-y-14">
          {personas.map((p) => (
            <div key={p.n} className="flex gap-6">
              <span className="font-display text-3xl font-light leading-none text-primary/80">
                {p.n}
              </span>
              <div className="flex-1 border-l border-border pl-6">
                <h3 className="font-display text-2xl font-medium tracking-tight">
                  {p.title}
                </h3>
                <ul className="mt-4 space-y-3 text-sm leading-relaxed text-foreground/80">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3">
                      <span className="mt-2 inline-block h-px w-3 shrink-0 bg-primary" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Garantias / Trust signals ─────────────────────────────────────────────
function Guarantees() {
  const items = [
    {
      title: 'Seus renders são privados',
      desc: 'Armazenamento isolado por conta, acesso apenas com link assinado. Nenhum render é usado pra treinar modelos.',
    },
    {
      title: 'Uso comercial livre',
      desc: 'Você pode usar os renders em portfólio, apresentações, anúncios, Instagram, mídia impressa — sem restrição.',
    },
    {
      title: 'NFS-e automática',
      desc: 'Emissão automática de nota fiscal em todo plano. Você lança direto na DRE sem dor de cabeça.',
    },
    {
      title: 'Cancele em um clique',
      desc: 'Sem fidelidade, sem multa, sem labirinto de suporte. Créditos do ciclo corrente seguem válidos até o fim.',
    },
    {
      title: 'PIX recorrente e cartão BR',
      desc: 'Cobrança em reais via Asaas. Aceita PIX, cartão nacional e boleto. Zero necessidade de cartão internacional.',
    },
    {
      title: 'Suporte em português',
      desc: 'WhatsApp direto pro time de produto nos planos Pro e Agência. Respondemos em minutos, não em dias.',
    },
  ];

  return (
    <section className="relative border-b border-border">
      <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-10 md:py-28">
        <div className="mb-14 grid grid-cols-1 gap-8 md:grid-cols-12">
          <div className="md:col-span-5">
            <span className="section-no block mb-4">§ 05 · Garantias</span>
            <h2 className="font-display text-4xl font-light leading-tight tracking-tight md:text-5xl">
              Sem letras
              <br />
              <span className="italic-drama text-primary">miúdas</span>.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <p className="text-lg leading-relaxed text-foreground/80">
              O Pórtico foi construído por quem também já perdeu três dias esperando render
              e apanhou de licença internacional. Aqui está tudo no claro, em português, com
              bandeira nacional e NF direto na DRE.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-y-10 md:grid-cols-3 md:gap-x-10 md:gap-y-14">
          {items.map((it) => (
            <div key={it.title} className="border-t border-border pt-6">
              <h3 className="font-display text-lg font-medium">{it.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground/75">{it.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Pricing — tabela editorial ─────────────────────────────────────────────
function Pricing() {
  const plans = [
    {
      name: 'Starter',
      price: '49',
      renders: '20 renders / mês',
      detail: 'Formato 16:9 · alta resolução · PIX',
      features: [
        'Qualidade Standard · alta resolução',
        'Resolução 2 784 × 1 504',
        'Suporte por e-mail',
        '30 dias de cancelamento livre',
      ],
      cta: 'Assinar Starter',
      link: PAYMENT_LINKS.starter,
      highlight: false,
    },
    {
      name: 'Pro',
      price: '149',
      renders: '80 renders / mês',
      detail: 'Formato 21:9 cinematic · variantes × 3 · prioridade',
      features: [
        'Qualidade Premium · formato 21:9',
        'Variants × 3 (3 opções por clique)',
        'Prioridade na fila',
        'Suporte WhatsApp direto',
        'Time-of-day modular',
      ],
      cta: 'Assinar Pro',
      link: PAYMENT_LINKS.pro,
      highlight: true,
    },
    {
      name: 'Agência',
      price: '499',
      renders: '350 renders / mês',
      detail: 'Multi-conta · branding · API · gerente dedicado',
      features: [
        'Tudo do Pro',
        'Até 5 contas de usuário',
        'Logomarca personalizada',
        'API REST privada',
        'Account manager dedicado',
        'NFS-e automática',
      ],
      cta: 'Assinar Agência',
      link: PAYMENT_LINKS.agencia,
      highlight: false,
    },
  ];

  return (
    <section id="pricing" className="relative border-b border-border bg-secondary/25">
      <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-10 md:py-32">
        <div className="mb-14 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="section-no block mb-4">§ 06 · Assinatura</span>
            <h2 className="font-display text-4xl font-light leading-tight tracking-tight md:text-5xl">
              Planos transparentes.
              <br />
              <span className="italic-drama text-primary">Cancelamento</span> em um clique.
            </h2>
          </div>
          <p className="max-w-xs text-sm text-muted-foreground">
            PIX ou cartão. Sem fidelidade. NFS-e. Comece com 3 renders
            grátis — sem cartão.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8">
          {plans.map((p) => (
            <article
              key={p.name}
              className={`relative flex flex-col border p-8 md:p-10 transition-all hover:shadow-[0_20px_60px_rgb(28_22_16_/_0.08)] ${
                p.highlight
                  ? 'border-foreground bg-card'
                  : 'border-border bg-card/60'
              }`}
            >
              {p.highlight && (
                <span className="absolute -top-3 left-8 bg-foreground px-3 py-1 font-mono text-[10px] uppercase tracking-[0.22em] text-background">
                  Recomendado
                </span>
              )}

              <div className="flex items-baseline justify-between border-b border-border pb-6">
                <h3 className="font-display text-2xl font-medium tracking-tight">
                  {p.name}
                </h3>
                <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                  {p.name === 'Pro' ? '+ Popular' : p.name === 'Starter' ? 'Individual' : 'Escritório'}
                </span>
              </div>

              <div className="mt-8">
                <div className="flex items-baseline gap-2">
                  <span className="font-mono text-sm text-muted-foreground">R$</span>
                  <span className="font-display text-6xl font-light leading-none tracking-tight text-foreground md:text-7xl">
                    {p.price}
                  </span>
                  <span className="font-mono text-xs text-muted-foreground">/mês</span>
                </div>
                <p className="mt-3 font-display italic text-primary">{p.renders}</p>
                <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  {p.detail}
                </p>
              </div>

              <ul className="mt-8 flex-1 space-y-3">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-foreground/85">
                    <span className="mt-1 inline-block h-px w-3 shrink-0 bg-primary" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <a
                href={p.link}
                target="_blank"
                rel="noreferrer"
                className={`mt-10 group inline-flex items-center justify-between border px-5 py-4 font-mono text-[11px] uppercase tracking-[0.22em] transition-all ${
                  p.highlight
                    ? 'border-foreground bg-foreground text-background hover:bg-primary hover:border-primary'
                    : 'border-border bg-transparent text-foreground hover:border-foreground'
                }`}
              >
                <span>{p.cta}</span>
                <span className="transition-transform group-hover:translate-x-0.5">→</span>
              </a>
            </article>
          ))}
        </div>

        <p className="mt-12 text-center text-sm text-muted-foreground">
          Volume maior, API dedicada, white-label?{' '}
          <a
            href={WA_LINK}
            target="_blank"
            rel="noreferrer"
            className="link-editorial font-medium text-foreground"
          >
            Fale com a gente
          </a>
          .
        </p>
      </div>
    </section>
  );
}

// ─── FAQ editorial ──────────────────────────────────────────────────────────
function FAQ() {
  const faqs = [
    {
      q: 'Funciona com Revit, AutoCAD, ArchiCAD, 3ds Max?',
      a: 'Sim. Basta exportar como imagem (PNG, JPG). SketchUp, Revit, ArchiCAD, 3ds Max, planta baixa, esboço à mão — tudo serve. Quanto mais definida a geometria de input, mais fiel o resultado.',
    },
    {
      q: 'Posso usar comercialmente os renders?',
      a: 'Sim, sem restrição. Portfólio, apresentação a cliente, Instagram, site, anúncio pago, mídia impressa. O que você gera é seu — não reivindicamos nenhum direito sobre os renders.',
    },
    {
      q: 'Como faço para cancelar?',
      a: 'No dashboard, um clique. Sem fidelidade, sem multa. Créditos não utilizados ficam válidos até o fim do ciclo pago.',
    },
    {
      q: 'Qual a diferença para Simplearchi, Veras, ArchiVinci?',
      a: 'A diferença está na curadoria. Afinamos nosso motor com referências visuais dos escritórios brasileiros que ditam tom no país — madeira oak com a cara certa, travertino, luz volumétrica de tarde, paisagismo tropical. Isso cria tons, texturas e composição com cara de Casa Vogue Brasil — e não de render genérico internacional. Pra fechar, entregamos em alta resolução pronta pra apresentação ou publicação.',
    },
    {
      q: 'A IA preserva exatamente o layout do meu modelo?',
      a: 'Sim. O motor é calibrado para preservar toda a geometria do seu input — ele só eleva materiais, texturas e iluminação para fotorrealista. Móveis, obras de arte e disposição são mantidos fielmente. Quanto mais definida for a geometria que você enviar, mais fiel o resultado final.',
    },
    {
      q: 'NFS-e? PIX? Posso lançar na DRE?',
      a: 'Sim. Emissão automática de NFS-e em todo plano. PIX e cartão brasileiro como métodos. Cobrança em reais. Processadora: Asaas (BR).',
    },
  ];

  return (
    <section className="relative border-b border-border">
      <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-10 md:py-32">
        <div className="grid grid-cols-1 gap-16 md:grid-cols-12">
          <div className="md:col-span-4">
            <span className="section-no block mb-4">§ 07 · Perguntas</span>
            <h2 className="font-display text-4xl font-light leading-tight tracking-tight md:text-5xl">
              Dúvidas
              <br />
              <span className="italic-drama text-primary">comuns</span>.
            </h2>
            <p className="mt-6 text-sm text-foreground/75">
              Não encontrou a resposta? Escreva para nós pelo{' '}
              <a
                href={WA_LINK}
                target="_blank"
                rel="noreferrer"
                className="link-editorial font-medium text-foreground"
              >
                WhatsApp
              </a>{' '}
              — respondemos em minutos.
            </p>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <div className="border-t border-border">
              {faqs.map((f, i) => (
                <details
                  key={f.q}
                  className="group border-b border-border py-6 transition-colors open:bg-secondary/30"
                >
                  <summary className="flex cursor-pointer list-none items-baseline justify-between gap-6 marker:hidden">
                    <div className="flex items-baseline gap-6">
                      <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span className="font-display text-xl font-medium tracking-tight md:text-2xl">
                        {f.q}
                      </span>
                    </div>
                    <span className="mt-1 shrink-0 font-display text-2xl font-light text-primary transition-transform group-open:rotate-45">
                      +
                    </span>
                  </summary>
                  <p className="mt-4 max-w-prose pl-10 text-base leading-relaxed text-foreground/80">
                    {f.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Closing CTA com atmosfera ───────────────────────────────────────────────
function LeadForm() {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'loading' | 'ok' | 'err'>('idle');
  const [msg, setMsg] = useState('');

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (state === 'loading') return;
    setState('loading');
    setMsg('');
    try {
      const res = await fetch(`${SUPA_URL}/functions/v1/capture-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${SUPA_ANON}` },
        body: JSON.stringify({ email, source: 'landing_closing' }),
      });
      const body = await res.json().catch(() => ({ ok: false }));
      if (res.ok && (body as { ok?: boolean }).ok) {
        setState('ok');
        setMsg('Mandei o convite no email. Clica no link pra criar conta e usar os 3 créditos.');
      } else {
        setState('err');
        setMsg((body as { error?: { message?: string } })?.error?.message ?? 'Não consegui enviar, tenta de novo.');
      }
    } catch {
      setState('err');
      setMsg('Falha de rede — tenta de novo.');
    }
  };

  return (
    <form onSubmit={submit} className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-stretch sm:justify-center">
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="seu@email.com.br"
        className="min-w-0 flex-1 max-w-sm border border-sidebar-foreground/30 bg-sidebar px-5 py-4 font-mono text-sm text-sidebar-foreground placeholder:text-sidebar-foreground/40 focus:border-primary focus:outline-none"
        autoComplete="email"
      />
      <button
        type="submit"
        disabled={state === 'loading' || state === 'ok'}
        className="group inline-flex items-center justify-center gap-3 bg-primary px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.22em] text-primary-foreground transition-all hover:bg-background hover:text-foreground disabled:opacity-60"
      >
        <span>{state === 'loading' ? 'Enviando…' : state === 'ok' ? 'Convite enviado' : 'Receber convite por email'}</span>
        {state !== 'ok' && <span className="transition-transform group-hover:translate-x-1">→</span>}
      </button>
      {msg && (
        <p className={`sm:basis-full sm:text-center text-sm ${state === 'ok' ? 'text-primary' : 'text-red-400'}`}>
          {msg}
        </p>
      )}
    </form>
  );
}

function ClosingCTA() {
  return (
    <section className="relative overflow-hidden bg-sidebar text-sidebar-foreground">
      <div className="pointer-events-none absolute inset-0 bg-grid-editorial opacity-20" />
      <div className="relative mx-auto max-w-[1400px] px-6 py-24 md:px-10 md:py-32">
        <div className="mx-auto max-w-3xl text-center">
          <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-sidebar-foreground/60">
            § 08 · Assine a Edição 001
          </span>
          <h2 className="mt-6 font-display text-4xl font-light leading-tight tracking-tight md:text-6xl">
            Seu próximo projeto
            <br />
            <span className="italic-drama text-primary">em trinta segundos.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-sidebar-foreground/80">
            Deixa o email que eu te mando o convite pra criar conta com 3 renders
            grátis. Sem cartão. Sem trial absurdo.
          </p>
          <LeadForm />
          <div className="mt-8 flex flex-col items-center justify-center gap-3 text-sm sm:flex-row sm:gap-6">
            <a
              href={SIGNUP_LINK}
              className="link-editorial font-mono text-xs uppercase tracking-[0.22em] text-sidebar-foreground/80"
            >
              Prefere criar conta direto →
            </a>
            <a
              href={LOGIN_LINK}
              className="link-editorial font-mono text-xs uppercase tracking-[0.22em] text-sidebar-foreground/60"
            >
              Já tenho conta
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Footer editorial ────────────────────────────────────────────────────────
function FooterBar() {
  return (
    <footer className="border-t border-border bg-background">
      <div className="mx-auto max-w-[1400px] px-6 py-14 md:px-10">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
          <div className="md:col-span-5">
            <a href="/" className="flex items-baseline gap-2 font-display text-2xl font-medium tracking-tight">
              <span className="text-primary">Pórtico</span>
            </a>
            <p className="mt-4 max-w-xs text-sm text-muted-foreground">
              Renderização arquitetônica por IA. Treinada em São Paulo,
              para o mundo.
            </p>
            <div className="mt-8 flex items-center gap-4">
              <a
                href={WA_LINK}
                target="_blank"
                rel="noreferrer"
                aria-label="WhatsApp"
                className="flex h-9 w-9 items-center justify-center border border-border transition-colors hover:border-foreground"
              >
                <span className="font-mono text-[10px] font-medium">WA</span>
              </a>
              <a
                href="https://instagram.com/portico"
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram"
                className="flex h-9 w-9 items-center justify-center border border-border transition-colors hover:border-foreground"
              >
                <span className="font-mono text-[10px] font-medium">IG</span>
              </a>
              <a
                href="mailto:contato@portico.studio"
                aria-label="Email"
                className="flex h-9 w-9 items-center justify-center border border-border transition-colors hover:border-foreground"
              >
                <span className="font-mono text-[10px] font-medium">@</span>
              </a>
            </div>
          </div>

          <div className="md:col-span-2 md:col-start-7">
            <h4 className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              Produto
            </h4>
            <ul className="mt-4 space-y-2 text-sm">
              <li><a href="#showcase" className="link-editorial text-foreground">Edições</a></li>
              <li><a href="#pricing" className="link-editorial text-foreground">Assinatura</a></li>
              <li><a href={LOGIN_LINK} className="link-editorial text-foreground">Entrar</a></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              Recursos
            </h4>
            <ul className="mt-4 space-y-2 text-sm">
              <li><a href="#showcase" className="link-editorial text-foreground">Edições</a></li>
              <li><a href={SIGNUP_LINK} className="link-editorial text-foreground">Testar grátis</a></li>
              <li><a href="mailto:suporte@portico.studio" className="link-editorial text-foreground">Suporte</a></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              Legal
            </h4>
            <ul className="mt-4 space-y-2 text-sm">
              <li><a href="/termos" className="link-editorial text-foreground">Termos de Uso</a></li>
              <li><a href="/privacidade" className="link-editorial text-foreground">Privacidade</a></li>
              <li><a href="/licenca" className="link-editorial text-foreground">Licença de Imagem</a></li>
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-border pt-8 text-xs text-muted-foreground md:flex-row md:items-center">
          <p>
            © {new Date().getFullYear()} Pórtico. Hecho em São Paulo.{' '}
            <span className="font-mono">
              CNPJ em constituição.
            </span>
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em]">
            v. 0.1.0 — Edição 001
          </p>
        </div>
      </div>
    </footer>
  );
}
